import type {
  PDCardCalibrationAudioResource,
  PDCardCalibrationErrorCode,
  PDCardCalibrationEventCode,
  PDCardCalibrationRefinedCardDetection,
} from '@zeelool/pd-card-calibration';

export const MEASUREMENT_SDK_VERSION = '1.0.30';

type IsExact<TLeft, TRight> =
  (<T>() => T extends TLeft ? 1 : 2) extends <T>() => T extends TRight ? 1 : 2 ? true : false;
type Assert<TCheck extends true> = TCheck;

export type MeasurementSdkRefinedCornersContract = Assert<
  IsExact<PDCardCalibrationRefinedCardDetection['cornersFitted'], boolean>
>;

export const MEASUREMENT_SDK_EVENT_CODE = {
  CenterFace: 'center-face-in-guide',
  MoveFaceRight: 'move-face-right',
  MoveFaceLeft: 'move-face-left',
  MoveFaceUp: 'move-face-up',
  MoveFaceDown: 'move-face-down',
  MoveCloser: 'move-closer-to-camera',
  MoveFarther: 'move-farther-from-camera',
  StraightenHead: 'straighten-head',
  TurnHeadRight: 'turn-head-right',
  TurnHeadLeft: 'turn-head-left',
  RaiseChin: 'raise-chin',
  LowerChin: 'lower-chin',
  CardNotDetected: 'card-not-detected',
  CardNotLevel: 'card-not-level',
  CardAspectRatioInvalid: 'card-aspect-ratio-invalid',
  Measuring: 'measuring-hold-still',
} as const satisfies Record<string, PDCardCalibrationEventCode>;

export const MEASUREMENT_SDK_ERROR_CODE = {
  CreateRuntimeLoadFailed: 'PD_CARD_CALIBRATION_CREATE_RUNTIME_LOAD_FAILED',
  CreateContainerInvalid: 'PD_CARD_CALIBRATION_CREATE_CONTAINER_INVALID',
  CreateEnvironmentUnsupported: 'PD_CARD_CALIBRATION_CREATE_ENVIRONMENT_UNSUPPORTED',
  CreateOptionsInvalid: 'PD_CARD_CALIBRATION_CREATE_OPTIONS_INVALID',
  CreateModeInvalid: 'PD_CARD_CALIBRATION_CREATE_MODE_INVALID',
  CreateOptionsConflict: 'PD_CARD_CALIBRATION_CREATE_OPTIONS_CONFLICT',
  CreateCardRoiInvalid: 'PD_CARD_CALIBRATION_CREATE_CARD_ROI_INVALID',
  CreateAudioResourcesInvalid: 'PD_CARD_CALIBRATION_CREATE_AUDIO_RESOURCES_INVALID',
  CreateBinocularPdRequired: 'PD_CARD_CALIBRATION_CREATE_BINOCULAR_PD_REQUIRED',
  CreateBinocularPdInvalid: 'PD_CARD_CALIBRATION_CREATE_BINOCULAR_PD_INVALID',
  CreateInternalFailed: 'PD_CARD_CALIBRATION_CREATE_INTERNAL_FAILED',
  InitInstanceDisposed: 'PD_CARD_CALIBRATION_INIT_INSTANCE_DISPOSED',
  InitCancelled: 'PD_CARD_CALIBRATION_INIT_CANCELLED',
  InitFaceWorkerLoadFailed: 'PD_CARD_CALIBRATION_INIT_FACE_WORKER_LOAD_FAILED',
  InitFaceWorkerTimeout: 'PD_CARD_CALIBRATION_INIT_FACE_WORKER_TIMEOUT',
  InitFaceWorkerCanvasUnsupported: 'PD_CARD_CALIBRATION_INIT_FACE_WORKER_CANVAS_UNSUPPORTED',
  InitFaceLandmarkerFailed: 'PD_CARD_CALIBRATION_INIT_FACE_LANDMARKER_FAILED',
  InitCardWorkerLoadFailed: 'PD_CARD_CALIBRATION_INIT_CARD_WORKER_LOAD_FAILED',
  InitCardWorkerTimeout: 'PD_CARD_CALIBRATION_INIT_CARD_WORKER_TIMEOUT',
  InitCardWorkerCanvasUnsupported: 'PD_CARD_CALIBRATION_INIT_CARD_WORKER_CANVAS_UNSUPPORTED',
  InitCardDetectorFailed: 'PD_CARD_CALIBRATION_INIT_CARD_DETECTOR_FAILED',
  InitFailed: 'PD_CARD_CALIBRATION_INIT_FAILED',
  StartInstanceDisposed: 'PD_CARD_CALIBRATION_START_INSTANCE_DISPOSED',
  StartNotInitialized: 'PD_CARD_CALIBRATION_START_NOT_INITIALIZED',
  StartCameraInsecureContext: 'PD_CARD_CALIBRATION_START_CAMERA_INSECURE_CONTEXT',
  StartCameraApiUnavailable: 'PD_CARD_CALIBRATION_START_CAMERA_API_UNAVAILABLE',
  StartCameraPermissionDenied: 'PD_CARD_CALIBRATION_START_CAMERA_PERMISSION_DENIED',
  StartCameraPermissionTimeout: 'PD_CARD_CALIBRATION_START_CAMERA_PERMISSION_TIMEOUT',
  StartCameraNotFound: 'PD_CARD_CALIBRATION_START_CAMERA_NOT_FOUND',
  StartCameraBusy: 'PD_CARD_CALIBRATION_START_CAMERA_BUSY',
  StartCameraNotReadable: 'PD_CARD_CALIBRATION_START_CAMERA_NOT_READABLE',
  StartCameraConstraintUnsupported: 'PD_CARD_CALIBRATION_START_CAMERA_CONSTRAINT_UNSUPPORTED',
  StartCameraDocumentInactive: 'PD_CARD_CALIBRATION_START_CAMERA_DOCUMENT_INACTIVE',
  StartCameraRequestInvalid: 'PD_CARD_CALIBRATION_START_CAMERA_REQUEST_INVALID',
  StartCameraPlayFailed: 'PD_CARD_CALIBRATION_START_CAMERA_PLAY_FAILED',
  StartCameraPlayTimeout: 'PD_CARD_CALIBRATION_START_CAMERA_PLAY_TIMEOUT',
  StartCameraMetadataTimeout: 'PD_CARD_CALIBRATION_START_CAMERA_METADATA_TIMEOUT',
  StartCameraFrameTimeout: 'PD_CARD_CALIBRATION_START_CAMERA_FRAME_TIMEOUT',
  StartCameraCancelled: 'PD_CARD_CALIBRATION_START_CAMERA_CANCELLED',
  StartCameraFailed: 'PD_CARD_CALIBRATION_START_CAMERA_FAILED',
  StartInternalFailed: 'PD_CARD_CALIBRATION_START_INTERNAL_FAILED',
} as const satisfies Record<string, PDCardCalibrationErrorCode>;

const MEASUREMENT_MEDIA_BASE_URL = 'https://common.xmslol.com/video/zeelool';
const MEASUREMENT_CHINESE_MEDIA_BASE_URL = 'https://common.xmslol.com/video/zeelool-pd-lang-cn';

const MEASUREMENT_AUDIO_FILE_BY_EVENT_CODE = {
  [MEASUREMENT_SDK_EVENT_CODE.CenterFace]: 'PD_measurement_step_1_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.MoveFaceRight]: 'PD_measurement_step_2_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.MoveFaceLeft]: 'PD_measurement_step_2_2.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.MoveFaceUp]: 'PD_measurement_step_3_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.MoveFaceDown]: 'PD_measurement_step_3_2.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.MoveCloser]: 'PD_measurement_step_4_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.MoveFarther]: 'PD_measurement_step_4_2.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.StraightenHead]: 'PD_measurement_step_5_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.TurnHeadRight]: 'PD_measurement_step_6_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.TurnHeadLeft]: 'PD_measurement_step_6_2.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.RaiseChin]: 'PD_measurement_step_7_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.LowerChin]: 'PD_measurement_step_7_2.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.CardNotDetected]: 'PD_measurement_step_8_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.CardNotLevel]: 'PD_measurement_step_9_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.CardAspectRatioInvalid]: 'PD_measurement_step_10_1.mp3',
  [MEASUREMENT_SDK_EVENT_CODE.Measuring]: 'PD_measurement_step_11_1.mp3',
} as const satisfies Record<PDCardCalibrationEventCode, string>;

function createMeasurementSdkAudioResources(
  mediaBaseUrl: string,
): readonly PDCardCalibrationAudioResource[] {
  return Object.entries(MEASUREMENT_AUDIO_FILE_BY_EVENT_CODE).map(([eventCode, fileName]) => ({
    eventCode: eventCode as PDCardCalibrationEventCode,
    audioPath: `${mediaBaseUrl}/${fileName}`,
  }));
}

export const MEASUREMENT_SDK_AUDIO_RESOURCES: readonly PDCardCalibrationAudioResource[] =
  createMeasurementSdkAudioResources(MEASUREMENT_MEDIA_BASE_URL);

export const MEASUREMENT_CHINESE_SDK_AUDIO_RESOURCES: readonly PDCardCalibrationAudioResource[] =
  createMeasurementSdkAudioResources(MEASUREMENT_CHINESE_MEDIA_BASE_URL);

export const MEASUREMENT_PREPARATION_VIDEO_URL = `${MEASUREMENT_MEDIA_BASE_URL}/PD_measurement.mp4`;
